// src/pages/orders/adminOrderDetails.js
// Admin order detail + staff actions (fulfill / tracking / cancel / refund),
// all driven by the commerce edge mutations.
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { Card, CardBody, CardHeader } from "../../components/card/card.jsx";
import {
  fetchCommerceOrder, fulfillAndTrack, cancelOrder, markOrderRefunded, markOrderPartiallyRefunded, addTracking,
} from "../../actions/commerce_admin_actions";

function money(m) {
  if (!m || !m.gross) return "—";
  return `${m.gross.currency} ${Number(m.gross.amount).toFixed(2)}`;
}
function addr(a) {
  if (!a) return "—";
  const parts = [
    [a.firstName, a.lastName].filter(Boolean).join(" "),
    a.streetAddress1, a.streetAddress2, a.city,
    [a.countryArea, a.postalCode].filter(Boolean).join(" "),
    a.country && a.country.country,
    a.phone,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

export default function AdminOrderDetails() {
  const { orderId } = useParams();
  const id = decodeURIComponent(orderId);
  const dispatch = useDispatch();
  const { detail, detailLoading, detailError, actionLoading, actionError } = useSelector((s) => s.commerceAdmin);

  useEffect(() => { dispatch(fetchCommerceOrder(id)); }, [dispatch, id]);

  if (detailLoading && !detail) return <div className="text-center py-5">Loading…</div>;
  if (detailError) return <div className="alert alert-danger">Could not load order: {detailError}</div>;
  if (!detail) return null;

  const o = detail;
  const status = o.status;
  const openFulfillment = (o.fulfillments || [])[0];

  const canFulfill = status === "unfulfilled";
  const canCancel = status !== "cancelled" && status !== "refunded";
  const canRefund = status !== "refunded";

  // Mark shipped = fulfil AND attach tracking, in one action.
  //
  // Tracking is REQUIRED here, and that is the point. orderFulfill on its own sends no
  // shipping email and does not release the PayPal hold — both happen only when the
  // tracking number is saved. Fulfilling without it ships the goods while the buyer's
  // money stays held. The main Orders page already refuses to submit without tracking;
  // this page used to let you do exactly that with two separate buttons.
  const doMarkShipped = () => {
    if (!canFulfill) return;
    const tn = (window.prompt(
      `Tracking number for order #${o.number}:\n\nRequired — it is what emails the customer and releases the PayPal hold.`,
    ) || "").trim();
    if (!tn) return;
    dispatch(fulfillAndTrack(id, tn));
  };

  // Kept for the follow-up cases the combined action cannot cover: adding tracking to
  // a fulfilment that already exists, or correcting a wrong number.
  const doTracking = () => {
    if (!openFulfillment) return;
    const tn = (window.prompt("Tracking number:") || "").trim();
    if (tn) dispatch(addTracking(openFulfillment.id, tn, id));
  };
  const doCancel = () => { if (window.confirm(`Cancel order #${o.number}? This releases its stock.`)) dispatch(cancelOrder(id)); };
  const doRefund = () => { if (window.confirm(`Mark order #${o.number} as refunded? (Money is refunded manually in the payment provider.)`)) dispatch(markOrderRefunded(id)); };
  const doPartialRefund = () => {
    const cur = (o.total && o.total.gross && o.total.gross.currency) || "USD";
    const raw = window.prompt(`Partial refund amount for order #${o.number} (${cur}):`);
    if (raw == null) return;
    const amt = Number(raw);
    if (!Number.isFinite(amt) || amt <= 0) { window.alert("Enter a positive amount."); return; }
    if (window.confirm(`Refund ${cur} ${amt.toFixed(2)} on order #${o.number}? (Money is refunded manually in the payment provider.)`)) dispatch(markOrderPartiallyRefunded(id, amt));
  };

  return (
    <div>
      <div className="mb-3">
        <Link to="/orders/manage" className="text-decoration-none"><i className="fa fa-chevron-left me-1" /> Back to orders</Link>
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div>
          <h1 className="page-header mb-0">Order #{o.number}</h1>
          <div className="text-inverse text-opacity-50">
            {o.userEmail} · <span className="badge bg-theme text-theme-900">{status}</span> · {o.paymentStatusDisplay} · from <span className="badge bg-indigo bg-opacity-25 text-indigo">{o.source || "—"}</span>
          </div>
        </div>
        <div className="btn-group">
          <button className="btn btn-outline-theme" disabled={!canFulfill || actionLoading} onClick={doMarkShipped}>Mark Shipped</button>
          <button className="btn btn-outline-theme" disabled={!openFulfillment || actionLoading} onClick={doTracking} title="Add or correct tracking on an existing fulfilment">Add Tracking</button>
          <button className="btn btn-outline-warning" disabled={!canCancel || actionLoading} onClick={doCancel}>Cancel</button>
          <button className="btn btn-outline-danger" disabled={!canRefund || actionLoading} onClick={doPartialRefund}>Partial Refund</button>
          <button className="btn btn-outline-danger" disabled={!canRefund || actionLoading} onClick={doRefund}>Mark Refunded</button>
        </div>
      </div>

      {actionError ? <div className="alert alert-danger">Action failed — {actionError}</div> : null}

      <div className="row">
        <div className="col-lg-8">
          <Card className="mb-3">
            <CardHeader>Items</CardHeader>
            <CardBody>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead><tr><th></th><th>Product</th><th className="text-center">Qty</th><th className="text-end">Unit</th><th className="text-end">Total</th></tr></thead>
                  <tbody>
                    {(o.lines || []).map((l, i) => (
                      <tr key={i}>
                        <td style={{ width: 56 }}>{l.thumbnail && l.thumbnail.url ? <img src={l.thumbnail.url} alt="" width="40" height="40" style={{ objectFit: "cover", borderRadius: 6 }} /> : null}</td>
                        <td><div className="fw-bold">{l.productName}</div><div className="small text-inverse text-opacity-50">{l.variantName} · {l.productSku}</div></td>
                        <td className="text-center">{l.quantity}</td>
                        <td className="text-end">{money(l.unitPrice)}</td>
                        <td className="text-end">{money(l.totalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>Fulfillments</CardHeader>
            <CardBody>
              {(o.fulfillments || []).length === 0 ? (
                <div className="text-inverse text-opacity-50">Not yet fulfilled.</div>
              ) : (
                <ul className="list-unstyled mb-0">
                  {o.fulfillments.map((f) => (
                    <li key={f.id} className="d-flex justify-content-between border-bottom py-2">
                      <span><span className="badge bg-success me-2">{f.status}</span></span>
                      <span className="text-inverse text-opacity-75">{f.trackingNumber ? `Tracking: ${f.trackingNumber}` : "no tracking yet"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="col-lg-4">
          <Card className="mb-3">
            <CardHeader>Summary</CardHeader>
            <CardBody>
              <div className="d-flex justify-content-between py-1"><span>Subtotal</span><span>{money(o.subtotal)}</span></div>
              <div className="d-flex justify-content-between py-1"><span>Shipping</span><span>{money(o.shippingPrice)}</span></div>
              {(o.discounts || []).length ? (() => {
                const cur = o.discounts[0].amount.currency || "";
                const total = o.discounts.reduce((s, d) => s + Number(d.amount.amount), 0);
                return <div className="d-flex justify-content-between py-1 text-success"><span>Discount</span><span>-{cur} {total.toFixed(2)}</span></div>;
              })() : null}
              {o.total && o.total.tax && Number(o.total.tax.amount) > 0
                ? <div className="d-flex justify-content-between py-1"><span>Tax</span><span>{o.total.tax.currency} {Number(o.total.tax.amount).toFixed(2)}</span></div>
                : null}
              <div className="d-flex justify-content-between py-1 fw-bold border-top mt-1 pt-2"><span>Total</span><span>{money(o.total)}</span></div>
              {o.totalRefunded && Number(o.totalRefunded.amount) > 0 ? <div className="d-flex justify-content-between py-1 text-warning"><span>Refunded</span><span>-{o.totalRefunded.currency} {Number(o.totalRefunded.amount).toFixed(2)}</span></div> : null}
              <div className="small text-inverse text-opacity-50 mt-2">{o.deliveryMethod && o.deliveryMethod.name ? `Delivery: ${o.deliveryMethod.name}` : null}</div>
            </CardBody>
          </Card>
          <Card className="mb-3">
            <CardHeader>Shipping address</CardHeader>
            <CardBody><div className="text-inverse text-opacity-75">{addr(o.shippingAddress)}</div></CardBody>
          </Card>
          <Card>
            <CardHeader>Billing address</CardHeader>
            <CardBody><div className="text-inverse text-opacity-75">{addr(o.billingAddress)}</div></CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
